<template>
  <Popover class="inline-flex shrink-0">
    <Float
      auto-placement
      portal
      :arrow="5"
      :offset="10"
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-1 opacity-0"
    >
      <PopoverButton
        class="border-0 text-amber-500 ring-0 hover:text-amber-600"
        :title="$t('invoicing.chift_send_failure_title')"
      >
        <FontAwesomeIcon :icon="['fas', 'triangle-exclamation']" />
      </PopoverButton>
      <PopoverPanel
        class="z-10 w-72 overflow-hidden rounded-md border border-gray-300 bg-white shadow-lg"
      >
        <FloatArrow
          class="absolute h-5 w-5 rotate-45 transform border border-gray-300 bg-white"
        />
        <div class="relative flex flex-col gap-1.5 bg-white p-3 text-xs">
          <div class="flex items-start gap-1.5">
            <h3 class="flex-1 text-sm font-bold text-gray-700">
              {{ $t('invoicing.chift_send_failure_title') }}
            </h3>
            <button
              type="button"
              class="shrink-0 text-gray-400 hover:text-gray-600"
              :title="$t('invoicing.chift_send_attempts_history')"
              @click="$emit('show-history')"
            >
              <FontAwesomeIcon :icon="['fal', 'clock-rotate-left']" />
            </button>
          </div>

          <span class="capitalize-first font-medium text-gray-500">
            {{ categoryLabel }}
          </span>

          <!-- The actionable wording, already rendered and translated by the
               backend at read time. Shown first: it is the only line telling the
               advisor what to do about the failure. -->
          <p v-if="failure.message" class="capitalize-first text-gray-700">
            {{ failure.message }}
          </p>

          <!-- What Chift literally says, in English. Kept apart from the
               actionable message on purpose: it is what matches the accounting
               software's own wording when support has to compare the two. -->
          <p v-if="failure.chift_description" class="text-gray-500 italic">
            {{ failure.chift_description }}
          </p>

          <hr class="border-gray-200" />

          <div class="flex items-baseline justify-between gap-2 text-gray-500">
            <span class="capitalize-first">
              {{ $t('invoicing.chift_send_failure_attempts', { count: failure.attempts }) }}
            </span>
            <span v-if="lastFailedAt" class="whitespace-nowrap">
              {{ lastFailedAt }}
            </span>
          </div>
        </div>
      </PopoverPanel>
    </Float>
  </Popover>
</template>

<script>
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { Float, FloatArrow } from '@headlessui-float/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'ChiftSendFailurePopover',
  components: {
    Popover,
    PopoverButton,
    PopoverPanel,
    Float,
    FloatArrow,
    FontAwesomeIcon
  },
  props: {
    /**
     * The invoice's latest unresolved failure, as InvoiceResource exposes it
     * under chift_send_failure:
     * { code, subcode, category, recoverable, message, chift_description,
     *   detail, attempts, first_failed_at, last_failed_at }.
     */
    failure: {
      type: Object,
      required: true
    }
  },
  emits: ['show-history'],
  computed: {
    /**
     * Categories come from ChiftErrorCategory. Falling back to the raw value
     * keeps a category added backend-side readable until its key is translated,
     * rather than printing the translation key on screen.
     */
    categoryLabel() {
      const key = `invoicing.chift_send_category_${this.failure.category}`
      const label = this.$t(key)

      return label === key ? this.failure.category : label
    },
    lastFailedAt() {
      if (!this.failure.last_failed_at) {
        return null
      }

      return this.$h.formatDate(
        this.failure.last_failed_at,
        'D MMM YYYY HH:mm',
        this.$i18n.locale.substring(0, 2)
      )
    }
  }
}
</script>
